import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, BookOpen, Lightbulb, Check, Send, Plus, Info } from 'lucide-react';

interface LearningSource {
  id?: string;
  title: string;
  type: string;
  url?: string;
  notes?: string;
}

interface AddEditLearningSourceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (source: LearningSource) => void;
  editingSource?: LearningSource | null;
  userRole?: 'employee' | 'manager';
}

const typeOptions = [
  { value: 'Book', label: 'Book' },
  { value: 'Article', label: 'Article' },
  { value: 'YouTube', label: 'YouTube Channel / Playlist' },
  { value: 'Podcast', label: 'Podcast' },
  { value: 'Internal', label: 'Internal Resource' },
];

const AddEditLearningSourceModal = ({ isOpen, onClose, onSubmit, editingSource, userRole = 'employee' }: AddEditLearningSourceModalProps) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<LearningSource>({
    title: '',
    type: 'Book',
    url: '',
    notes: '',
  });

  useEffect(() => {
    if (editingSource) {
      setFormData({
        title: editingSource.title || '',
        type: editingSource.type || 'Book',
        url: editingSource.url || '',
        notes: editingSource.notes || '',
      });
    } else {
      setFormData({
        title: '',
        type: 'Book',
        url: '',
        notes: '',
      });
    }
  }, [editingSource, isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.title && formData.type) {
      onSubmit(formData);
      setFormData({ title: '', type: 'Book', url: '', notes: '' });
    }
  };

  const handleClose = () => {
    setFormData({ title: '', type: 'Book', url: '', notes: '' });
    onClose();
  };

  const isManager = userRole === 'manager';
  const modalTitle = editingSource ? t('Edit Learning Source') : isManager ? t('Add Learning Source') : t('Suggest Learning Source');
  const modalSubtitle = editingSource
    ? t('Update resource details')
    : isManager
      ? t('Add a resource to support development')
      : t('Suggest a resource for manager confirmation');
  const submitButtonText = editingSource ? t('Update Source') : isManager ? t('Add Source') : t('Submit Suggestion');

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-border sticky top-0 bg-white z-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isManager ? 'bg-blue-100' : 'bg-orange-100'}`}>
                {isManager ? <BookOpen className="w-5 h-5 text-blue-600" /> : <Lightbulb className="w-5 h-5 text-orange-600" />}
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900">{modalTitle}</h2>
                <p className="text-sm text-gray-600">{modalSubtitle}</p>
              </div>
            </div>
            <button onClick={handleClose} className="text-gray-600 hover:text-gray-900 transition-colors">
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-2">
              {t('Title')}
            </label>
            <input
              type="text"
              placeholder={t('e.g., Data Visualization Best Practices')}
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
              className="w-full px-3 py-2 border border-border rounded-lg text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-900 mb-2">
              {t('Type')}
            </label>
            <select
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value })}
              required
              className="w-full px-3 py-2 border border-border rounded-lg text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
            >
              {typeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {t(option.label)}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-900 mb-2">
              {t('URL (Optional)')}
            </label>
            <input
              type="url"
              placeholder={t('https://example.com/resource')}
              value={formData.url}
              onChange={(e) => setFormData({ ...formData, url: e.target.value })}
              className="w-full px-3 py-2 border border-border rounded-lg text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
            />
            <p className="text-xs text-gray-600 mt-1">{t('Link to the learning resource')}</p>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-900">
              {t('Notes (Optional)')}
            </label>
            <textarea
              className="w-full h-24 rounded-md border border-border bg-white px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
              placeholder={t('Brief description of why this resource is helpful...')}
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            />
            <p className="text-xs text-gray-600">{t('Provide context about how this resource supports the development objective')}</p>
          </div>

          {/* Permission Notice */}
          {!isManager && !editingSource && (
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 flex items-start space-x-2">
              <Info className="w-4 h-4 text-orange-600 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-orange-800">
                <strong>{t('Note')}:</strong> {t('Your suggestion will be reviewed by your manager before being added to the development objective.')}
              </p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-border">
            <button type="button" onClick={handleClose} className="px-4 py-2 border border-border rounded-lg hover:bg-gray-50">
              {t('Cancel')}
            </button>
            <button
              type="submit"
              disabled={!formData.title || !formData.type}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {editingSource ? <Check className="w-4 h-4" /> : isManager ? <Plus className="w-4 h-4" /> : <Send className="w-4 h-4" />}
              {submitButtonText}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddEditLearningSourceModal;
